import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { Td } from "../../features/web3/forex/components/table";
import { formatDateTime } from "../../lib/format";
import { useCan, useForexAdmin } from "./api";
import { diffValues, listChange } from "./logic";
import { BASE, ListView, linkCls, mono, muted } from "./parts";

// 026's actions, with its display labels.
const ACTIONS = {
  "role.created": "Created a role",
  "role.edited": "Edited a role",
  "role.deleted": "Deleted a role",
  "operator.granted": "Granted an operator",
  "operator.changed": "Changed an operator",
  "operator.revoked": "Revoked an operator",
  "copying.paused": "Paused copying",
  "copying.resumed": "Resumed copying",
  "problem.resolved": "Resolved a problem",
  "master.opened": "Opened a master",
  "master.hidden": "Hid a master",
  "master.unhidden": "Unhid a master",
  "master.switch_started": "Provider switch started",
  "master.switch_done": "Provider switch done",
  "master.switch_undone": "Provider switch undone",
  "master.switch_stuck": "Provider switch stuck",
  "connection.ended": "Ended a connection",
};
const RECORD_TYPES = ["role", "operator", "copying", "problem", "master", "connection"];

const ACTION_OPTIONS = [{ value: "", label: "All actions" }, ...Object.entries(ACTIONS).map(([value, label]) => ({ value, label }))];
const RECORD_OPTIONS = [{ value: "", label: "All records" }, ...RECORD_TYPES.map((v) => ({ value: v, label: v }))];

// Where each record type's page is, and the permission that opens it. A
// resolved problem leaves the problems list, so it links nowhere.
const RECORD_PAGE = {
  role: [() => `${BASE}/roles`, ["roles.manage", "operators.manage"]],
  operator: [() => `${BASE}/operators`, "operators.manage"],
  copying: [() => `${BASE}/copy-engine`, "copy_engine.read"],
  master: [(id) => `${BASE}/masters/${id}`, "masters.read"],
  connection: [(id) => `${BASE}/connections/${id}`, "connections.read"],
};

function Record({ type, id }) {
  const can = useCan();
  const page = RECORD_PAGE[type];
  const text = (
    <>
      {type} <span className="text-muted-foreground" title={id}>{id.slice(0, 8)}</span>
    </>
  );
  if (!page || !can(page[1])) return <span title={id}>{text}</span>;
  return <Link to={page[0](id)} className={linkCls} title={id}>{text}</Link>;
}

const Actor = ({ a }) =>
  a.type === "operator" ? (
    <span title={a.operatorId}>{a.name}</span>
  ) : (
    <span>Master key <span className="text-muted-foreground">{a.address}</span></span>
  );

const show = (v) => (v === undefined ? "—" : JSON.stringify(v));

// Old and new side by side, every key, the changed ones marked. A role's
// permissions read as added and taken away; a roleId reads as its role's name
// when the roles list is open to the operator.
function Values({ e, roleName }) {
  const rows = diffValues(e.oldValues, e.newValues);
  if (rows.length === 0) return <p className="text-xs text-muted-foreground">No values.</p>;
  const value = (key, v) => (key === "roleId" && roleName(v) ? `${roleName(v)} (${v})` : show(v));
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-left text-muted-foreground">
          <th className="py-1 pr-4 font-medium">Key</th>
          <th className="py-1 pr-4 font-medium">Before</th>
          <th className="py-1 font-medium">After</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} className={r.changed ? "bg-amber-500/[0.08]" : undefined}>
            <td className="py-1 pr-4 font-mono">{r.key}</td>
            {/* Only an edit has both lists; a create or delete shows its one list as is. */}
            {r.key === "permissions" && Array.isArray(r.old) && Array.isArray(r.new) ? (
              <td colSpan={2} className="py-1 font-mono">
                {(() => {
                  const { added, removed } = listChange(r.old, r.new);
                  if (!added.length && !removed.length) return <span className="text-muted-foreground">unchanged</span>;
                  return (
                    <>
                      {added.map((p) => <div key={p} className="text-emerald-400">+ {p}</div>)}
                      {removed.map((p) => <div key={p} className="text-red-400">− {p}</div>)}
                    </>
                  );
                })()}
              </td>
            ) : (
              <>
                <td className="break-all py-1 pr-4 font-mono">{value(r.key, r.old)}</td>
                <td className="break-all py-1 font-mono">{value(r.key, r.new)}</td>
              </>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const COLUMNS = [{ label: "When" }, { label: "Action" }, { label: "By" }, { label: "Record" }, { label: "Request id" }, { label: "" }];

function EntryRow({ e, roleName }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="hover:bg-white/[0.02]">
        <Td className={muted}>{formatDateTime(e.createdAt)}</Td>
        <Td className="whitespace-nowrap">{ACTIONS[e.action] ?? e.action}</Td>
        <Td className="whitespace-nowrap"><Actor a={e.actor} /></Td>
        <Td className={mono}><Record type={e.recordType} id={e.recordId} /></Td>
        <Td className={muted}>{e.requestId}</Td>
        <Td className="text-xs">
          <button type="button" onClick={() => setOpen((o) => !o)} className={linkCls}>
            {open ? "Hide" : "Values"}
          </button>
        </Td>
      </tr>
      {open && (
        <tr>
          <td colSpan={COLUMNS.length} className="border-b border-border/40 px-5 py-3">
            <Values e={e} roleName={roleName} />
          </td>
        </tr>
      )}
    </>
  );
}

// roleId → name, from the roles list, which only a manager may read.
function useRoleName() {
  const can = useCan();
  const roles = useForexAdmin(can(["roles.manage", "operators.manage"]) ? "/roles/admin" : null);
  return (id) => roles.data?.find((r) => r.id === id)?.name;
}

// One record's history, for its own page (026): the trail with the record fixed.
export function RecordHistory({ type, id }) {
  const roleName = useRoleName();
  return (
    <ListView
      path="/audit/admin"
      empty={{}}
      fields={[]}
      fixed={{ recordType: type, recordId: id }}
      columns={COLUMNS}
      row={(e) => <EntryRow key={e.id} e={e} roleName={roleName} />}
      label="entries"
      emptyText="No operator has changed this yet."
    />
  );
}

export default function Audit() {
  const can = useCan();
  const qc = useQueryClient();
  const roleName = useRoleName();
  // Pick the operator by name when their list is open to us; else type the id.
  const operators = useForexAdmin(can("operators.manage") ? "/operators/admin" : null);
  const operatorField = operators.data
    ? {
        name: "operatorId",
        label: "Operator",
        options: [{ value: "", label: "All operators" }, ...operators.data.map((o) => ({ value: o.operatorId, label: o.name }))],
      }
    : { name: "operatorId", label: "Operator id", placeholder: "24 hex characters" };

  return (
    <QueueLayout
      eyebrow="Forex Admin · Access"
      title="Audit trail"
      description="Every change an operator or the master key made, newest first. Reads and refused requests are not recorded. A record id needs its record type."
      onRefresh={() => qc.invalidateQueries({ queryKey: ["forex-admin"] })}
    >
      <ListView
        path="/audit/admin"
        empty={{ operatorId: "", action: "", recordType: "", recordId: "", from: "", to: "" }}
        fields={[
          operatorField,
          { name: "action", label: "Action", options: ACTION_OPTIONS },
          { name: "recordType", label: "Record type", options: RECORD_OPTIONS },
          { name: "recordId", label: "Record id" },
          { name: "from", label: "From", type: "datetime-local" },
          { name: "to", label: "To", type: "datetime-local" },
        ]}
        columns={COLUMNS}
        row={(e) => <EntryRow key={e.id} e={e} roleName={roleName} />}
        label="entries"
        emptyText="No entry matches."
      />
    </QueueLayout>
  );
}
